/**
 * MarketFitAnalysisPage
 * 
 * Dedicated page for analyzing a single Deal artifact by connecting
 * Product Solutions to Customer Situations with Actor alignment.
 * 
 * Layout:
 * - Score Header: deal_score, product_situation_alignment, value, actor_alignment
 * - Left Panel: Solution (inputs top, benefits bottom)
 * - Right Panel: Situation (resources top-left, needs bottom-left, actor center, inputs/outputs right)
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Typography, 
  Spin, 
  Empty,
  message,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  AimOutlined,
  SyncOutlined,
  LinkOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { MarketFitProvider, useMarketFit } from '../../contexts/MarketFitContext';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { 
  ScoreHeader, 
  SolutionPanel, 
  SituationPanel,
  ConnectionLinksManager,
  MarketFitSettingsDrawer,
} from '../../components/MarketFitAnalysis';
import { useDashboardNavigation } from '../../contexts/DashboardNavigationContext';
import styles from './MarketFitAnalysisPage.module.css';

const { Title, Text } = Typography;

// ============================================================================
// INNER COMPONENT (uses context)
// ============================================================================

const MarketFitAnalysisContent: React.FC = () => {
  const { productId, dealId } = useParams<{ productId: string; dealId: string }>();
  const navigate = useNavigate();
  const { goBack, currentLocation } = useDashboardNavigation();
  
  const {
    scores,
    solution,
    situation,
    connectionLinks,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    error,
    setSolution,
    setSituation,
    setActor,
    autoDetectLinks,
    saveDeal,
    setActorSituationAlignment,
  } = useMarketFit();

  const [linksManagerVisible, setLinksManagerVisible] = useState(false);
  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);

  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      // In a real implementation, show confirmation dialog
      void message.warning('You have unsaved changes');
    }
    goBack();
  }, [goBack, hasUnsavedChanges]);

  const handleSave = useCallback(async () => {
    await saveDeal();
    void message.success('Deal saved successfully');
  }, [saveDeal]);

  const handleAutoDetect = useCallback(() => {
    autoDetectLinks();
    void message.success('Links auto-detected');
  }, [autoDetectLinks]);

  const handleDealCopied = useCallback((newDealId: string, newDealName: string) => {
    setSettingsDrawerVisible(false);
    navigate(`/dashboard/product/${productId}/marketfit/analysis/${newDealId}`);
    void message.success(`Navigating to copied deal: ${newDealName}`);
  }, [navigate, productId]);

  if (!productId || !dealId) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description="No deal selected"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Empty>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <Text className={styles.loadingText}>Loading deal analysis...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Empty
          description={error}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Empty>
      </div>
    );
  }

  const productName = currentLocation.productName || 'Product';

  return (
    <div className={styles.analysisPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Tooltip title="Back to MarketFit">
            <button className={styles.backButton} onClick={handleBack}>
              <ArrowLeftOutlined />
            </button>
          </Tooltip>
          <div className={styles.headerIcon}>
            <AimOutlined />
          </div>
          <div className={styles.headerTitle}>
            <Title level={3} className={styles.title}>
              MarketFit Analysis
            </Title>
            <Text className={styles.subtitle}>
              {productName} • {solution?.name ?? 'Loading...'}
            </Text>
          </div>
        </div>
        <div className={styles.headerActions}>
          {hasUnsavedChanges && (
            <div className={styles.unsavedIndicator}>
              <span className={styles.unsavedDot} />
              <span>Unsaved changes</span>
            </div>
          )}
          <Tooltip title="Auto-detect links based on name similarity">
            <Button 
              icon={<SyncOutlined />}
              onClick={handleAutoDetect}
              className={styles.autoDetectButton}
            >
              Auto-Detect
            </Button>
          </Tooltip>
          <Button 
            icon={<LinkOutlined />}
            onClick={() => setLinksManagerVisible(true)}
          >
            Manage Links
          </Button>
          <Tooltip title="Deal Settings">
            <Button 
              icon={<SettingOutlined />}
              onClick={() => setSettingsDrawerVisible(true)}
            />
          </Tooltip>
          <Button 
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            className={styles.saveButton}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Score Header */}
      <ScoreHeader scores={scores} isLoading={isLoading} />

      {/* Main Content - Two Panels */}
      <div className={styles.mainContent}>
        {/* Left Panel - Solution */}
        <SolutionPanel
          solution={solution}
          inputLinks={connectionLinks.solutionInputToSituationResourceLinks}
          benefitLinks={connectionLinks.solutionBenefitToSituationNeedLinks}
        />

        {/* Right Panel - Situation */}
        <SituationPanel
          situation={situation}
          resourceLinks={connectionLinks.solutionInputToSituationResourceLinks}
          needLinks={connectionLinks.solutionBenefitToSituationNeedLinks}
        />
      </div>

      {/* Connection Links Manager Modal */}
      <ConnectionLinksManager
        visible={linksManagerVisible}
        onClose={() => setLinksManagerVisible(false)}
      />

      {/* Settings Drawer */}
      <MarketFitSettingsDrawer
        visible={settingsDrawerVisible}
        onClose={() => setSettingsDrawerVisible(false)}
        dealId={dealId}
        productId={productId}
        onDealCopied={handleDealCopied}
      />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT (provides context)
// ============================================================================

export const MarketFitAnalysisPage: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();

  return (
    <MarketFitProvider dealId={dealId} useLocalBridge={isLocalBridgeApiEnabled()}>
      <MarketFitAnalysisContent />
    </MarketFitProvider>
  );
};

export default MarketFitAnalysisPage;
